import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../../api/client';
import { AlertItem } from '../../types';
import { DataTable } from '../../components/common/DataTable';
import { StatusBadge } from '../../components/common/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import { ColumnDef } from '@tanstack/react-table';
import { Check, VideoOff } from 'lucide-react';

export const AlertsPage: React.FC = () => {
  const { can } = useAuth();
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [acking, setAcking] = useState<string | null>(null);

  useEffect(() => {
    loadAlerts();
  }, []);

  const loadAlerts = async () => {
    try {
      const data = await api.getAlerts();
      setAlerts(data);
    } catch (err) {
      console.error('Failed to load alerts', err);
    }
  };

  const ackOne = async (a: AlertItem) => {
    setAcking(a.id);
    try {
      await api.ackAlert(a.id);
      setAlerts((prev) => prev.map((x) => (x.id === a.id ? { ...x, status: 'acknowledged' } : x)));
    } catch (err) {
      console.error('Failed to acknowledge alert', err);
    } finally {
      setAcking(null);
    }
  };

  const columns = useMemo<ColumnDef<AlertItem>[]>(
    () => [
      {
        accessorKey: 'type',
        header: 'Event Classification',
        cell: (info) => (
          <div>
            <strong style={{ textTransform: 'capitalize', color: 'var(--text-main)' }}>
              {(info.getValue() as string).replace('_', ' ')}
            </strong>
            <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>
              ID: {info.row.original.id}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'severity',
        header: 'Severity',
        cell: (info) => <StatusBadge status={info.getValue() as string} />,
      },
      {
        accessorKey: 'deviceName',
        header: 'Camera Device',
        cell: (info) => {
          const a = info.row.original;
          return (
            <div>
              <div style={{ fontWeight: 500 }}>{a.deviceName}</div>
              <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>
                <code>{a.deviceSerial}</code> • {a.ownerName}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'message',
        header: 'Telemetry & Event Metadata',
        cell: (info) => (
          <span style={{ fontSize: '12.5px', color: 'var(--text-main)' }}>
            {info.getValue() as string}
          </span>
        ),
      },
      {
        accessorKey: 'timestamp',
        header: 'Timestamp',
        cell: (info) => (
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            {new Date(info.getValue() as string).toLocaleString()}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: (info) => <StatusBadge status={info.getValue() as string} />,
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
          const a = info.row.original;
          if (a.status === 'acknowledged') {
            return <span style={{ fontSize: '11px', color: 'var(--text-dim)' }}>Acknowledged</span>;
          }
          return (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => ackOne(a)}
              disabled={!can('alerts:manage') || acking === a.id}
            >
              <Check size={13} />
              <span>{acking === a.id ? 'Acking…' : 'Acknowledge'}</span>
            </button>
          );
        },
      },
    ],
    [can, acking]
  );

  return (
    <div>
      {/* Footage Firewall Banner */}
      <div className="firewall-banner" style={{ marginBottom: '16px' }}>
        <VideoOff size={15} color="var(--primary-light)" />
        <div>
          <strong>Footage Firewall:</strong> Alert details display event metadata and timestamp logs only. Live streaming and clip playback are restricted from admin views.
        </div>
      </div>

      <DataTable
        storageKey="alertsStream"
        data={alerts}
        columns={columns}
        searchPlaceholder="Search event type, camera, message..."
        exportFilename="mwm_alerts_log.csv"
      />
    </div>
  );
};
